import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Volume2 } from "lucide-react";
import { Switch } from "./ui/switch";
import { useAuthedApi, errMsg } from "../lib/api";
import { soundOn, setSoundOn, playChime, pushSupported, currentSub, enablePush, disablePush, isIOS, isStandalone } from "../lib/notify";

export default function NotificationSettings() {
  const api = useAuthedApi();
  const [sound, setSound] = useState(soundOn);
  const [push, setPush] = useState(false);
  const [busy, setBusy] = useState(false);
  const supported = pushSupported();
  const blocked = supported && Notification.permission === "denied";

  useEffect(() => { currentSub().then((s) => setPush(!!s && Notification.permission === "granted")).catch(() => {}); }, []);

  const toggleSound = (v) => {
    setSound(v); setSoundOn(v);
    api("put", "/me/prefs", { sound: v }).catch(() => {});
    if (v) playChime(true);
  };
  const togglePush = async (v) => {
    setBusy(true);
    try {
      if (v) { await enablePush(api); setPush(true); toast.success("Notifications turned on for this device"); }
      else { await disablePush(api); setPush(false); toast.success("Notifications turned off for this device"); }
    } catch (e) { toast.error(e.message || errMsg(e)); }
    setBusy(false);
  };
  const test = async () => {
    try { await api("post", "/push/test"); toast.success("Test notification sent"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4" data-testid="notification-settings">
      <label className="flex items-center justify-between gap-4 cursor-pointer">
        <span className="flex items-center gap-3 text-sm"><Volume2 className="w-4 h-4 text-[#D4AF37]" /><span>Sound for new messages & offers<span className="block text-xs text-gray-500">Plays a short chime while the site is open</span></span></span>
        <Switch className="data-[state=unchecked]:bg-gray-600" checked={sound} onCheckedChange={toggleSound} data-testid="notif-sound-toggle" />
      </label>
      <label className="flex items-center justify-between gap-4 cursor-pointer">
        <span className="flex items-center gap-3 text-sm"><BellRing className="w-4 h-4 text-[#D4AF37]" /><span>Phone & browser notifications<span className="block text-xs text-gray-500">Alerts even when the site is closed. Tap one to open the chat.</span></span></span>
        <Switch className="data-[state=unchecked]:bg-gray-600" checked={push} disabled={busy || (!supported && !isIOS())} onCheckedChange={togglePush} data-testid="notif-push-toggle" />
      </label>
      {!supported && isIOS() && !isStandalone() && <p className="text-xs text-amber-400" data-testid="notif-ios-hint">On iPhone: tap Share → Add to Home Screen, open the app from your Home Screen, then turn this on.</p>}
      {!supported && !isIOS() && <p className="text-xs text-amber-400" data-testid="notif-unsupported">This browser doesn't support notifications.</p>}
      {blocked && <p className="text-xs text-amber-400" data-testid="notif-blocked">Notifications are blocked for this site. Allow them in your browser/phone settings, then turn this on.</p>}
      {push && <button type="button" className="btn-outline text-xs !py-1.5" onClick={test} data-testid="notif-test-btn">Send a test notification</button>}
    </div>
  );
}
